<script setup lang="ts">
import type Schema from "~/types/Schema";
import type APIResponseData from "~/types/APIResponseData";
import type SchemaChild from "~/types/SchemaChild";
import { computed, type PropType } from "vue";
import schemaLink from "~/utils/schemaLink";
import type Character from "~/types/Character";
import type Prefix from "~/types/Prefix";
import type Suffix from "~/types/Suffix";
import type DataType from "~/types/DataType";
import type SchemaParent from "~/types/SchemaParent";
import { useSideNavStore } from "~/store/sideNavStore";

type FieldDataType = Schema | DataType | Character | Prefix | Suffix;
type ItemsType = APIResponseData<FieldDataType>[] | SchemaChild[] | SchemaParent[] | null;
const props = defineProps({
  items: {
    type: Array as PropType<ItemsType> | null,
    default: () => [],
  },
  applyRowLink: {
    type: Boolean,
    default: true,
  },
});

function isNotEmpty(item: APIResponseData<FieldDataType> | SchemaChild | SchemaParent) {
  if (item?.fieldData && (item.fieldData?.name || item.fieldData?.hex)) {
    return true;
  }
  if (item?.childCamelCaseName && item.childCamelCaseName !== "") {
    return true;
  }
  if (item?.parentName && item.parentName !== "") {
    return true;
  }

  return false;
}

// Filter out empty items
const filteredItems = computed(() => props.items?.filter(isNotEmpty));

const sideNavStore = useSideNavStore();
const useXl = computed(() => sideNavStore.expanded);
</script>

<template>
  <div class="box-border rounded-t-lg border">
    <div
      class="hidden border-b px-4 py-4 text-lg font-bold last:border-b-0 even:bg-gray-50"
      :class="useXl ? 'xl:flex' : 'lg:flex'"
    >
      <slot name="header"></slot>
    </div>

    <div v-if="filteredItems?.length > 0">
      <div
        v-for="item in filteredItems"
        :key="item.recordId"
        class="border-b last:border-b-0 odd:bg-gray-50 hover:bg-gray-100"
        :class="applyRowLink && schemaLink(item) ? 'cursor-pointer hover:text-cyan-700' : ''"
      >
        <NuxtLink
          v-if="applyRowLink && schemaLink(item)"
          :to="schemaLink(item)"
          class="block px-4 py-4"
          :class="useXl ? 'xl:flex' : 'lg:flex'"
        >
          <slot name="row" :item="item" :use-x-l="useXl"></slot>
        </NuxtLink>

        <div v-else class="block px-4 py-4" :class="useXl ? 'xl:flex ' : 'lg:flex'">
          <slot name="row" :item="item" :use-x-l="useXl"></slot>
        </div>
      </div>
    </div>

    <div v-else class="py-4 text-center font-light text-grc-gray-darker">
      No results found... Try different keywords...
    </div>
  </div>
</template>
